import { useCustomFilters } from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllScheduleAssets } from "../../../../hooks";
import { AssetFilterPanel } from "../../filters/AssetFilterPanel";

interface ScheduleAssetsTabProps {
  scheduleId: string;
}

interface Asset {
  id: string;
  name: string;
  type: string;
  location: string;
  conditionScore: number;
  riskScore: number;
  lastServiced: string;
}

const getConditionColor = (score: number) => {
  if (score >= 85) return "bg-green-100 text-green-800";
  if (score >= 70) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

const getConditionLabel = (score: number) => {
  if (score >= 85) return "Good";
  if (score >= 70) return "Fair";
  return "Poor";
};

const getRiskColor = (score: number) => {
  if (score >= 70) return "bg-red-100 text-red-800";
  if (score >= 50) return "bg-yellow-100 text-yellow-800";
  return "bg-green-100 text-green-800";
};

const getRiskLabel = (score: number) => {
  if (score >= 70) return "High";
  if (score >= 50) return "Medium";
  return "Low";
};

export const ScheduleAssetsTab: React.FC<ScheduleAssetsTabProps> = ({
  scheduleId,
}) => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();

  // Memoize the filter configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        assetType: "assetType",
        search: "query",
        page: "page",
      },
      defaults: {
        page: DEFAULT_PAGE_NUMBER,
        limit: PAGE_SIZE,
        assetType: "all",
        search: "",
      },
      parsers: {
        assetType: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        assetType: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  // Use the configurable URL filter utility
  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  const { data: assetsData, isLoading } = useGetAllScheduleAssets(
    scheduleId,
    page,
    PAGE_SIZE,
    filters.search_data
  );

  // Pagination calculations based on API data
  const pagination = useMemo(() => {
    if (!assetsData) {
      return {
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        itemsPerPage: PAGE_SIZE,
        startItem: 0,
        endItem: 0,
      };
    }

    const totalPages = Math.ceil((assetsData.totalCount || 0) / PAGE_SIZE);
    const startItem = (page - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(page * PAGE_SIZE, assetsData.totalCount || 0);

    return {
      currentPage: page,
      totalPages,
      totalItems: assetsData.totalCount || 0,
      itemsPerPage: PAGE_SIZE,
      startItem,
      endItem,
    };
  }, [assetsData, page]);

  // Action handlers
  const handleViewAssetDetails = (asset: Asset) => {
    navigate(`/ax/asset-registry/assets/view/${asset.id}`);
  };

  const handleExport = () => {
    console.log("Exporting assets data");
    // Implement export functionality
  };

  // Table actions configuration
  const actions: TableAction<Asset>[] = [
    {
      label: "View Asset Details",
      onClick: (asset: Asset) => handleViewAssetDetails(asset),
    },
  ];

  // Define table columns
  const columns: TableColumn<Asset>[] = [
    {
      key: "assetNo",
      header: "Asset ID",
      type: "text",
    },
    {
      key: "assetName",
      header: "Asset Name",
      type: "text",
    },
    {
      key: "assetTypeDisplay",
      header: "Asset Type",
      type: "text",
    },
    {
      key: "location",
      header: "Location",
      type: "text",
    },
    {
      key: "conditionScore",
      header: "Condition Score",
      type: "custom",
      render: (value: number, row: Asset) => (
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              value >= 85
                ? "bg-green-500"
                : value >= 70
                ? "bg-yellow-500"
                : "bg-red-500"
            }`}
          ></div>
          <span className="font-medium">{value}</span>
          <Badge className={getConditionColor(value)}>
            {getConditionLabel(value)}
          </Badge>
        </div>
      ),
    },
    {
      key: "riskScore",
      header: "Risk Score",
      type: "custom",
      render: (value: number, row: Asset) => (
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              value >= 70
                ? "bg-red-500"
                : value >= 50
                ? "bg-yellow-500"
                : "bg-green-500"
            }`}
          ></div>
          <span className="font-medium">{value}</span>
          <Badge className={getRiskColor(value)}>{getRiskLabel(value)}</Badge>
        </div>
      ),
    },
    {
      key: "lastServiced",
      header: "Last Serviced",
      type: "text",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="page-title mb-2">Associated Assets</CardTitle>
        <p className="page-subtitle mb-2">
          This schedule applies to the following {assetsData?.totalCount || 0}{" "}
          assets.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <GenericTable
          data={assetsData?.results || []}
          columns={columns}
          rowKey="id"
          searchPlaceholder="Search by Asset ID, Name, Location..."
          searchParamName="query"
          searchDebounceMs={300}
          onSearch={handleSearch}
          pagination={pagination}
          onPageChange={handlePageChange}
          actions={actions}
          actionsType="dropdown"
          filterPanel={
            <AssetFilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          // onExport={handleExport}
          emptyMessage="No assets found matching your criteria"
          loading={isLoading}
        />
      </CardContent>
    </Card>
  );
};
